/* Принять оплату: на кассе (Kaspi QR, карта, наличные) или ссылкой на онлайн-оплату (Kaspi Pay, Freedom Pay, тестовая). */
import { useState } from "react";
import { METHODS, depositFor, money, totals, type Order } from "@okno/shared";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import type { StartPaymentResult } from "../../lib/types";
import { Icon } from "../../ui/Icon";
import { Modal, useCloseLayer } from "../../ui/layers";
import { Spinner } from "../../ui/kit";
import { useToast } from "../../ui/toast";
import { currencySign, useOrderApi, useOrderDetails } from "./useOrder";

type PayType = "payment" | "deposit";
type Mode = "cash" | "online";

const parseAmount = (s: string) => parseInt(s.replace(/\D/g, ""), 10) || 0;

export interface PaymentModalProps {
  orderId: string;
  /** Вызывается, когда после оплаты на кассе долг погашен (например, «Выдать и закрыть»). */
  after?: () => void;
}

export function PaymentModal({ orderId, after }: PaymentModalProps) {
  const q = useOrderDetails(orderId);
  if (!q.data) return <Modal small title="Принять оплату"><Spinner /></Modal>;
  return <PaymentForm order={q.data.order} phone={q.data.customer.phone} after={after} />;
}

function PaymentForm({ order: o, phone, after }: { order: Order; phone: string; after?: () => void }) {
  const { boot } = useSession();
  const close = useCloseLayer();
  const toast = useToast();
  const { path, run } = useOrderApi(o.id);
  const providers = boot.channels.payments;
  const t = totals(o, boot.settings);
  const deposit = depositFor(t.total, boot.settings);
  const canDeposit = o.status !== "ready" && t.paid === 0 && !["in_work", "arrived"].includes(o.status) && deposit < t.due;

  const [mode, setMode] = useState<Mode>("cash");
  const [type, setType] = useState<PayType>("payment");
  const [amountStr, setAmountStr] = useState(String(t.due));
  const [method, setMethod] = useState(METHODS[0]);
  const [providerId, setProviderId] = useState(providers[0]?.id || "");
  const [sendSms, setSendSms] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<StartPaymentResult | null>(null);
  const amount = parseAmount(amountStr);

  const pickType = (v: PayType) => {
    setType(v);
    setAmountStr(String(v === "deposit" ? deposit : t.due));
    setInvalid(false);
  };

  const validAmount = () => {
    if (amount > 0 && amount <= t.due) return true;
    setInvalid(true);
    toast("Сумма должна быть от 1 до " + money(t.due), "bad");
    return false;
  };

  const payCash = async () => {
    if (!validAmount()) return;
    setBusy(true);
    const res = await run(() => api.post<Order>(path + "/payments", { type, method, amount }));
    setBusy(false);
    if (!res) return;
    close();
    toast((type === "deposit" ? "Депозит " : "Оплата ") + money(amount) + " принята");
    if (after && totals(res, boot.settings).due === 0) after();
  };

  const makeLink = async () => {
    if (!validAmount()) return;
    if (!providerId) return toast("Нет подключённой онлайн-оплаты", "bad");
    setBusy(true);
    const res = await run(() => api.post<StartPaymentResult>(path + "/payment-link", { type, amount, providerId, sendSms }));
    setBusy(false);
    if (res) setResult(res);
  };

  if (result) return <LinkResult order={o} result={result} phone={phone} onDone={close} />;

  const providerName = (id: string) => providers.find((p) => p.id === id)?.name || id;
  const foot = (
    <>
      <button className="btn" onClick={close}>
        Отмена
      </button>
      {mode === "cash" ? (
        <button className="btn btn-primary" disabled={busy} onClick={() => void payCash()}>
          Принять {money(amount)}
        </button>
      ) : (
        <button className="btn btn-primary" disabled={busy || !providerId} onClick={() => void makeLink()}>
          <Icon name={sendSms ? "send" : "external"} size={16} />
          {sendSms ? "Отправить ссылку" : "Создать ссылку"}
        </button>
      )}
    </>
  );

  return (
    <Modal small title="Принять оплату" sub={"Заказ №" + o.num + " · к оплате " + money(t.due)} foot={foot}>
      <div className="stack">
        {providers.length ? (
          <div className="segmented" role="group" aria-label="Где платит клиент">
            <button aria-pressed={mode === "cash"} onClick={() => setMode("cash")}>
              На кассе
            </button>
            <button aria-pressed={mode === "online"} onClick={() => setMode("online")}>
              Ссылка на оплату
            </button>
          </div>
        ) : null}
        {canDeposit ? (
          <div className="segmented" role="group" aria-label="Тип платежа">
            <button aria-pressed={type === "payment"} onClick={() => pickType("payment")}>
              Полная оплата
            </button>
            <button aria-pressed={type === "deposit"} onClick={() => pickType("deposit")}>
              Депозит {Math.round(boot.settings.depositShare * 100)}%
            </button>
          </div>
        ) : null}
        <label className="field">
          <span>Сумма, {currencySign()}</span>
          <input
            className="input"
            inputMode="numeric"
            autoFocus
            value={amountStr}
            aria-invalid={invalid || undefined}
            onChange={(e) => {
              setAmountStr(e.target.value);
              setInvalid(false);
            }}
          />
        </label>
        {mode === "cash" ? (
          <div className="field">
            <span>Способ</span>
            <div className="segmented" role="group" aria-label="Способ оплаты">
              {METHODS.map((m) => (
                <button key={m} aria-pressed={method === m} onClick={() => setMethod(m)}>
                  {m}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {providers.length > 1 ? (
              <div className="field">
                <span>Провайдер</span>
                <div className="segmented" role="group" aria-label="Провайдер оплаты">
                  {providers.map((p) => (
                    <button key={p.id} aria-pressed={providerId === p.id} onClick={() => setProviderId(p.id)}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="muted" style={{ fontSize: 14 }}>
                Провайдер: {providerName(providerId)}
              </div>
            )}
            {boot.channels.sms ? (
              <label className="row" style={{ gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={sendSms} onChange={(e) => setSendSms(e.target.checked)} />
                Отправить ссылку клиенту в SMS
              </label>
            ) : null}
            <div className="callout">
              <Icon name="info" size={16} />
              <span>Оплата проведётся автоматически, когда провайдер подтвердит платёж. До этого в карточке заказа платёж «ожидает оплаты».</span>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** Результат выставления счёта: ссылка (копировать/открыть) или инструкция Kaspi, итог SMS. */
function LinkResult({ order, result, phone, onDone }: { order: Order; result: StartPaymentResult; phone: string; onDone: () => void }) {
  const { boot } = useSession();
  const toast = useToast();
  const name = boot.channels.payments.find((p) => p.id === result.provider)?.name || result.provider;
  const copy = async () => {
    if (!result.payUrl) return;
    try {
      await navigator.clipboard.writeText(result.payUrl);
      toast("Ссылка скопирована");
    } catch {
      toast("Не удалось скопировать — выделите ссылку вручную", "bad");
    }
  };
  const smsOk = result.sms && result.sms.status !== "failed";
  return (
    <Modal
      small
      title="Счёт выставлен"
      sub={"Заказ №" + order.num + " · " + name + " · " + money(result.amount)}
      foot={
        <button className="btn btn-primary" onClick={onDone}>
          Готово
        </button>
      }
    >
      <div className="stack">
        {result.payUrl ? (
          <div className="field">
            <span>Ссылка на оплату</span>
            <div className="row" style={{ flexWrap: "nowrap" }}>
              <input className="input" readOnly value={result.payUrl} onFocus={(e) => e.currentTarget.select()} aria-label="Ссылка на оплату" />
              <button className="btn btn-sm" onClick={() => void copy()}>
                Копировать
              </button>
              <a className="btn btn-sm" href={result.payUrl} target="_blank" rel="noopener noreferrer">
                <Icon name="external" size={16} />
                Открыть
              </a>
            </div>
          </div>
        ) : null}
        {result.instructions ? (
          <div className="callout callout-signal">
            <Icon name="qr" size={16} />
            <span>
              Клиент оплачивает в приложении Kaspi по номеру заказа №{order.num}: {result.instructions}
            </span>
          </div>
        ) : null}
        {result.sms ? (
          <div className={"callout" + (smsOk ? "" : " callout-bad")}>
            <Icon name={smsOk ? "check" : "alert"} size={16} />
            <span>{smsOk ? "SMS со ссылкой отправлено на " + phone : "SMS не отправлено: " + (result.sms.error || "ошибка провайдера")}</span>
          </div>
        ) : null}
        <p className="muted" style={{ fontSize: 14 }}>
          Платёж появится в карточке заказа как «ожидает оплаты» и проведётся сам после подтверждения провайдера. Если долго нет — нажмите «Проверить».
        </p>
      </div>
    </Modal>
  );
}
